/**
 * TalentOS — react-query hooks.
 *
 * Both reads are free and idempotent (no scoring, no writes, no enqueue), so
 * they are safe to mount, unmount and remount as a modal opens and closes.
 */
import { useQuery } from '@tanstack/react-query'
import { talentosApi } from './talentosApi'
import type { TalentosMatchSummary } from './talentosApi'

/**
 * Scoring is expensive and append-only: a run does not change once written, and
 * a new one only appears when someone re-runs matching. Five minutes of
 * freshness keeps a modal that opens repeatedly from re-fetching each time
 * without ever showing a figure that could have silently moved.
 */
const STALE_MS = 5 * 60 * 1000

/** The full report for one candidate. `data === null` means "not scored yet";
 *  `data === undefined` means still loading. Distinguish the two — collapsing
 *  them renders "not scored" during every load. */
export function useTalentosMatch(jobId: string | undefined, resumeId: string | undefined) {
  return useQuery({
    queryKey: ['talentos', 'match', jobId, resumeId],
    queryFn: () => talentosApi.getLatestMatch(jobId!, resumeId!),
    // Both ids come from a selected row; the hook may mount before one exists.
    enabled: Boolean(jobId && resumeId),
    staleTime: STALE_MS,
    // getLatestMatch already turns 404 into null, so anything reaching here is
    // a real failure. One retry covers a transient blip without making a
    // genuinely broken backend take four round-trips to report.
    retry: 1,
  })
}

/**
 * Every scored candidate on a job, keyed by `host_resume_id` for O(1) lookup
 * while rendering a list.
 *
 * Returns a Map rather than the raw array precisely because the caller is
 * inside a render loop over candidates: an array would mean a linear scan per
 * row, and the quadratic version of this is easy to write by accident.
 */
export function useTalentosMatchesForJob(jobId: string | undefined) {
  const query = useQuery({
    queryKey: ['talentos', 'matches-by-job', jobId],
    queryFn: () => talentosApi.getMatchesForJob(jobId!),
    enabled: Boolean(jobId),
    staleTime: STALE_MS,
    retry: 1,
  })

  const byResumeId = new Map<string, TalentosMatchSummary>(
    (query.data?.results ?? []).map((result) => [result.host_resume_id, result]),
  )

  return { ...query, byResumeId }
}
